import React, { useState, useEffect, useCallback } from 'react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassSelect } from '../ui/GlassSelect';
import { apiClient } from '../../services/api';
import { CheckCircle, XCircle, RefreshCw, Save, ShieldCheck } from 'lucide-react';
import './ModelConfigPanel.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ── Types ────────────────────────────────────────────────────────────────────

interface SupportedModel {
  provider: string;
  model_id: string;
  label: string;
  default: boolean;
  cost_tier?: string;
}

interface ModelsCatalogue {
  text: SupportedModel[];
  coordinator: SupportedModel[];
  image: SupportedModel[];
  video: SupportedModel[];
}

interface SystemConfigEntry {
  model_type: string;
  provider: string | null;
  model_id: string | null;
  has_key: boolean;
  is_active: boolean;
  updated_at: string | null;
}

interface SystemConfig {
  text: SystemConfigEntry;
  coordinator: SystemConfigEntry;
  image: SystemConfigEntry;
  video: SystemConfigEntry;
}

interface FallbackLogEntry {
  id: number;
  user_id: string;
  model_type: string;
  attempted_provider: string;
  error_summary: string | null;
  fell_back_to: string | null;
  created_at: string | null;
}

type ModelType = 'text' | 'coordinator' | 'image' | 'video';

const MODEL_TYPE_LABELS: Record<ModelType, string> = {
  text: 'Text Generation',
  coordinator: 'Coordinator Brain',
  image: 'Image Generation',
  video: 'Video Generation',
};

const MODEL_TYPES: ModelType[] = ['text', 'coordinator', 'image', 'video'];

const PROVIDER_LABELS: Record<string, string> = {
  gemini: 'Gemini',
  claude: 'Claude (Anthropic)',
  openai: 'OpenAI',
  grok: 'Grok (xAI)',
  dall_e: 'DALL-E (OpenAI)',
  stability: 'Stability AI',
  replicate: 'Replicate (Flux)',
  google: 'Google (Imagen)',
  runway: 'RunwayML',
  pika: 'Pika Labs',
  kling: 'Kling AI',
};

// ── Row state ────────────────────────────────────────────────────────────────

interface RowState {
  provider: string;
  model_id: string;
  api_key: string;
  testStatus: 'idle' | 'testing' | 'valid' | 'invalid';
  testError: string | null;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  saveError: string | null;
}

// ── Component ────────────────────────────────────────────────────────────────

const ModelConfigPanel: React.FC = () => {
  const [catalogue, setCatalogue] = useState<ModelsCatalogue | null>(null);
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null);
  const [fallbackLog, setFallbackLog] = useState<FallbackLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Per-type row state
  const [rows, setRows] = useState<Record<ModelType, RowState>>({
    text: { provider: '', model_id: '', api_key: '', testStatus: 'idle', testError: null, saveStatus: 'idle', saveError: null },
    coordinator: { provider: '', model_id: '', api_key: '', testStatus: 'idle', testError: null, saveStatus: 'idle', saveError: null },
    image: { provider: '', model_id: '', api_key: '', testStatus: 'idle', testError: null, saveStatus: 'idle', saveError: null },
    video: { provider: '', model_id: '', api_key: '', testStatus: 'idle', testError: null, saveStatus: 'idle', saveError: null },
  });

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [modelsRes, sysRes, logRes] = await Promise.all([
        apiClient.get('/model-config/models'),
        apiClient.get('/model-config/system'),
        apiClient.get('/model-config/fallback-log'),
      ]);
      const cat: ModelsCatalogue = modelsRes.data;
      const sys: SystemConfig = sysRes.data;
      setCatalogue(cat);
      setSystemConfig(sys);
      setFallbackLog(logRes.data);

      // Seed row state from system config
      setRows(prev => {
        const next = { ...prev };
        MODEL_TYPES.forEach(mt => {
          const entry = sys[mt];
          next[mt] = {
            ...prev[mt],
            provider: entry.provider || '',
            model_id: entry.model_id || '',
            api_key: '',  // never pre-fill key
          };
        });
        return next;
      });
    } catch (err: any) {
      setLoadError(getApiError(err, 'Failed to load model configuration'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Row helpers ────────────────────────────────────────────────────────────

  const updateRow = (mt: ModelType, patch: Partial<RowState>) => {
    setRows(prev => ({ ...prev, [mt]: { ...prev[mt], ...patch } }));
  };

  const getProviderOptions = (mt: ModelType) => {
    if (!catalogue) return [];
    const seen = new Set<string>();
    return catalogue[mt]
      .filter(m => { if (seen.has(m.provider)) return false; seen.add(m.provider); return true; })
      .map(m => ({ value: m.provider, label: PROVIDER_LABELS[m.provider] || m.provider }));
  };

  const getModelOptions = (mt: ModelType, provider: string) => {
    if (!catalogue) return [];
    return catalogue[mt]
      .filter(m => m.provider === provider)
      .map(m => ({ value: m.model_id, label: m.label }));
  };

  const handleProviderChange = (mt: ModelType, provider: string) => {
    // Auto-select first model for new provider
    const firstModel = catalogue?.[mt].find(m => m.provider === provider);
    updateRow(mt, {
      provider,
      model_id: firstModel?.model_id || '',
      testStatus: 'idle',
      testError: null,
      saveStatus: 'idle',
      saveError: null,
    });
  };

  const handleModelChange = (mt: ModelType, model_id: string) => {
    updateRow(mt, { model_id, testStatus: 'idle', testError: null });
  };

  const handleTest = async (mt: ModelType) => {
    const row = rows[mt];
    if (!row.provider || !row.model_id || !row.api_key) return;
    updateRow(mt, { testStatus: 'testing', testError: null });
    try {
      const res = await apiClient.post('/model-config/system/validate-key', {
        provider: row.provider,
        model_id: row.model_id,
        api_key: row.api_key,
      });
      if (res.data.valid) {
        updateRow(mt, { testStatus: 'valid', testError: null });
      } else {
        updateRow(mt, { testStatus: 'invalid', testError: res.data.error || 'Invalid key' });
      }
    } catch (err: any) {
      updateRow(mt, { testStatus: 'invalid', testError: getApiError(err, 'Test failed') });
    }
  };

  const handleSave = async (mt: ModelType) => {
    const row = rows[mt];
    if (!row.provider || !row.model_id) return;
    updateRow(mt, { saveStatus: 'saving', saveError: null });
    try {
      await apiClient.put(`/model-config/system/${mt}`, {
        provider: row.provider,
        model_id: row.model_id,
        api_key: row.api_key || null,
      });
      updateRow(mt, { saveStatus: 'saved', saveError: null, api_key: '' });
      // Refresh system config to update has_key indicator
      const sysRes = await apiClient.get('/model-config/system');
      setSystemConfig(sysRes.data);
    } catch (err: any) {
      updateRow(mt, { saveStatus: 'error', saveError: getApiError(err, 'Save failed') });
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <GlassCard padding="lg">
        <p style={{ color: 'var(--text-secondary)' }}>Loading model configuration...</p>
      </GlassCard>
    );
  }

  if (loadError) {
    return (
      <GlassCard padding="lg">
        <p style={{ color: 'var(--danger)' }}>{loadError}</p>
        <GlassButton variant="secondary" onClick={loadAll} icon={<RefreshCw size={16} />}>
          Retry
        </GlassButton>
      </GlassCard>
    );
  }

  return (
    <div className="model-config-panel">
      {/* Header */}
      <div className="model-config-header">
        <h3 className="model-config-title">
          <ShieldCheck size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
          Model Configuration
        </h3>
        <p className="model-config-subtitle">
          Configure which AI models power each function. API keys are encrypted at rest and never returned by the API.
        </p>
        <GlassButton variant="secondary" size="sm" onClick={loadAll} icon={<RefreshCw size={14} />}>
          Refresh
        </GlassButton>
      </div>

      {/* System Model Configuration */}
      <GlassCard padding="lg" className="model-config-section">
        <h4 className="model-config-section-title">System Model Configuration</h4>

        <div className="model-config-rows">
          {MODEL_TYPES.map(mt => {
            const row = rows[mt];
            const configEntry = systemConfig?.[mt];
            const providerOptions = getProviderOptions(mt);
            const modelOptions = getModelOptions(mt, row.provider);

            return (
              <div key={mt} className="model-config-row">
                {/* Type label */}
                <div className="model-config-row-label">
                  <span className="model-type-name">{MODEL_TYPE_LABELS[mt]}</span>
                </div>

                {/* Provider dropdown */}
                <div className="model-config-row-field">
                  <GlassSelect
                    label="Provider"
                    options={providerOptions}
                    value={row.provider}
                    onChange={v => handleProviderChange(mt, v)}
                    size="sm"
                  />
                </div>

                {/* Model dropdown */}
                <div className="model-config-row-field">
                  <GlassSelect
                    label="Model"
                    options={modelOptions}
                    value={row.model_id}
                    onChange={v => handleModelChange(mt, v)}
                    size="sm"
                    disabled={!row.provider}
                  />
                </div>

                {/* API key input */}
                <div className="model-config-row-field model-config-key-field">
                  <GlassInput
                    label="API Key"
                    type="password"
                    value={row.api_key}
                    onChange={v => updateRow(mt, { api_key: v, testStatus: 'idle', testError: null })}
                    placeholder={
                      configEntry?.has_key
                        ? '● Key saved — enter new to replace'
                        : 'Enter API key (leave blank to use env var)'
                    }
                  />
                  {configEntry?.has_key && !row.api_key && (
                    <span className="key-saved-badge">
                      <CheckCircle size={13} /> Key saved
                    </span>
                  )}
                </div>

                {/* Test result */}
                <div className="model-config-row-status">
                  {row.testStatus === 'valid' && (
                    <span className="test-result test-valid">
                      <CheckCircle size={14} /> Valid
                    </span>
                  )}
                  {row.testStatus === 'invalid' && (
                    <span className="test-result test-invalid" title={row.testError || ''}>
                      <XCircle size={14} /> {row.testError ? row.testError.slice(0, 40) : 'Invalid'}
                    </span>
                  )}
                  {row.saveStatus === 'saved' && row.testStatus === 'idle' && (
                    <span className="test-result test-valid">
                      <CheckCircle size={14} /> Saved
                    </span>
                  )}
                  {row.saveStatus === 'error' && (
                    <span className="test-result test-invalid" title={row.saveError || ''}>
                      <XCircle size={14} /> {row.saveError ? row.saveError.slice(0, 40) : 'Error'}
                    </span>
                  )}
                </div>

                {/* Buttons */}
                <div className="model-config-row-actions">
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={() => handleTest(mt)}
                    disabled={!row.api_key || !row.provider || !row.model_id || row.testStatus === 'testing'}
                    icon={row.testStatus === 'testing' ? <RefreshCw size={14} className="spin" /> : undefined}
                  >
                    {row.testStatus === 'testing' ? 'Testing...' : 'Test'}
                  </GlassButton>
                  <GlassButton
                    variant="primary"
                    size="sm"
                    onClick={() => handleSave(mt)}
                    disabled={!row.provider || !row.model_id || row.saveStatus === 'saving'}
                    icon={row.saveStatus === 'saving' ? <RefreshCw size={14} className="spin" /> : <Save size={14} />}
                  >
                    {row.saveStatus === 'saving' ? 'Saving...' : 'Save'}
                  </GlassButton>
                </div>
              </div>
            );
          })}
        </div>
      </GlassCard>

      {/* Fallback Activity Log */}
      <GlassCard padding="lg" className="model-config-section">
        <h4 className="model-config-section-title">Fallback Activity Log</h4>
        <p className="model-config-section-desc">
          Last 100 model fallback events across all users.
        </p>

        {fallbackLog.length === 0 ? (
          <div className="fallback-empty">No fallback events recorded.</div>
        ) : (
          <div className="fallback-table-wrap">
            <table className="fallback-table">
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>User ID</th>
                  <th>Type</th>
                  <th>Attempted</th>
                  <th>Fell Back To</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {fallbackLog.map(entry => (
                  <tr key={entry.id}>
                    <td className="fallback-cell-mono">
                      {entry.created_at
                        ? new Date(entry.created_at).toLocaleString()
                        : '—'}
                    </td>
                    <td className="fallback-cell-mono">
                      {entry.user_id ? `${entry.user_id.slice(0, 8)}…` : '—'}
                    </td>
                    <td>{entry.model_type}</td>
                    <td>{entry.attempted_provider}</td>
                    <td>{entry.fell_back_to || '—'}</td>
                    <td className="fallback-cell-error" title={entry.error_summary || ''}>
                      {entry.error_summary
                        ? entry.error_summary.slice(0, 60)
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
    </div>
  );
};

export default ModelConfigPanel;
